import React, { useState } from 'react';

import { storeAuth, getStoredAuthProfile, apiFetch, parseJsonResponse } from '../constants';

import { tUi } from '../utils/uiText';

export default function LoginGate({ onSuccess }) {
  const uiLanguage = 'en';

  const [step, setStep] = useState('credentials');

  const [organizationId, setOrganizationId] = useState('');

  const [accessKey, setAccessKey] = useState('');

  const [totpCode, setTotpCode] = useState('');

  const [backupCode, setBackupCode] = useState('');

  const [useBackupCode, setUseBackupCode] = useState(false);

  const [mfaPendingToken, setMfaPendingToken] = useState('');

  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  const [manualSecret, setManualSecret] = useState('');

  const [provisioningUri, setProvisioningUri] = useState('');

  const [backupCodes, setBackupCodes] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const resetMfaState = () => {
    setStep('credentials');
    setTotpCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setMfaPendingToken('');
    setMfaEnrollmentRequired(false);
    setProvisioningUri('');
    setQrCodeDataUrl('');
    setManualSecret('');
    setBackupCodes([]);
  };

  const finishLogin = (data) => {
    storeAuth(data);
    onSuccess(getStoredAuthProfile());
  };

  const handleCredentialSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          organization_id: organizationId.trim(),
          access_key: accessKey.trim(),
        }),
      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(tUi(uiLanguage, 'loginInvalidCredentials'));
        }
        throw new Error(
          data.detail
            || ((response.headers.get('content-type') || '').includes('text/html')
              ? tUi(uiLanguage, 'loginUnableToSignIn')
              : tUi(uiLanguage, 'loginSignInFailed')),
        );
      }

      if (data.mfa_required || data.mfa_pending_token) {
        if (!data.mfa_pending_token) {
          throw new Error(tUi(uiLanguage, 'mfaVerificationFailed'));
        }
        setMfaPendingToken(data.mfa_pending_token);
        setMfaEnrollmentRequired(Boolean(data.mfa_enrollment_required));
        setProvisioningUri(data.provisioning_uri || '');
        setQrCodeDataUrl(data.qr_code_data_url || '');
        setManualSecret(data.manual_secret || '');
        setStep(data.mfa_enrollment_required ? 'mfa_enroll' : 'mfa_verify');
        return;
      }

      if (!data.access_token) {
        throw new Error(tUi(uiLanguage, 'loginUnableToSignIn'));
      }

      finishLogin(data);
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'loginUnableToSignIn'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    const endpoint = mfaEnrollmentRequired ? '/api/auth/mfa/enroll' : '/api/auth/mfa/verify';
    const body = { mfa_pending_token: mfaPendingToken };

    if (mfaEnrollmentRequired) {
      body.totp_code = totpCode.trim();
    } else if (useBackupCode) {
      body.backup_code = backupCode.trim();
    } else {
      body.totp_code = totpCode.trim();
    }

    try {
      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.detail || tUi(uiLanguage, 'mfaVerificationFailed'));
      }

      if (Array.isArray(data.backup_codes) && data.backup_codes.length > 0) {
        setBackupCodes(data.backup_codes);
        storeAuth(data);
        setStep('backup_codes');
        return;
      }

      finishLogin(data);
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'mfaVerificationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const renderCredentialsForm = () => (
    <form onSubmit={handleCredentialSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {tUi(uiLanguage, 'loginOrganizationId')}
        </label>
        <input
          type="text"
          value={organizationId}
          onChange={(event) => setOrganizationId(event.target.value)}
          required
          autoComplete="username"
          className="w-full px-4 py-2 bg-legend-deep border border-legend-teal/30 rounded-lg focus:ring-2 focus:ring-legend-teal text-legend-paper"
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {tUi(uiLanguage, 'loginAccessKey')}
        </label>
        <input
          type="password"
          value={accessKey}
          onChange={(event) => setAccessKey(event.target.value)}
          required
          autoComplete="current-password"
          className="w-full px-4 py-2 bg-legend-deep border border-legend-teal/30 rounded-lg focus:ring-2 focus:ring-legend-teal text-legend-paper"
          disabled={loading}
        />
      </div>

      {error && (
        <div className="text-sm text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !organizationId.trim() || !accessKey.trim()}
        className="w-full px-4 py-2 bg-legend-teal hover:bg-legend-teal-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
      >
        {loading ? tUi(uiLanguage, 'loginSigningIn') : tUi(uiLanguage, 'loginSignIn')}
      </button>
    </form>
  );

  const renderMfaForm = () => (
    <form onSubmit={handleMfaSubmit} className="space-y-4">
      {step === 'mfa_enroll' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-300">{tUi(uiLanguage, 'mfaEnrollInstructions')}</p>
          {qrCodeDataUrl ? (
            <img
              src={qrCodeDataUrl}
              alt={tUi(uiLanguage, 'mfaQrAlt')}
              className="mx-auto w-48 h-48 bg-white rounded-lg p-2"
            />
          ) : null}
          {manualSecret ? (
            <div className="text-xs text-gray-400 break-all bg-gray-900 border border-gray-700 rounded-lg p-3">
              <span className="block text-gray-300 mb-1">{tUi(uiLanguage, 'mfaManualSecret')}</span>
              {manualSecret}
            </div>
          ) : null}
          {provisioningUri ? (
            <p className="text-[10px] text-gray-500 break-all">{provisioningUri}</p>
          ) : null}
        </div>
      )}

      {step === 'mfa_verify' && !useBackupCode && (
        <p className="text-sm text-gray-300">{tUi(uiLanguage, 'mfaVerifyInstructions')}</p>
      )}

      {!useBackupCode ? (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {tUi(uiLanguage, 'mfaAuthenticatorCode')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, ''))}
            required={!useBackupCode}
            autoComplete="one-time-code"
    className="w-full px-4 py-2 bg-legend-deep border border-legend-teal/30 rounded-lg focus:ring-2 focus:ring-legend-teal text-legend-paper tracking-widest text-center text-lg"
            disabled={loading}
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {tUi(uiLanguage, 'mfaBackupCode')}
          </label>
          <input
            type="text"
            value={backupCode}
            onChange={(event) => setBackupCode(event.target.value.toUpperCase())}
            required
            className="w-full px-4 py-2 bg-legend-deep border border-legend-teal/30 rounded-lg focus:ring-2 focus:ring-legend-teal text-legend-paper"
            disabled={loading}
          />
        </div>
      )}

      {step === 'mfa_verify' && (
        <button
          type="button"
          onClick={() => {
            setUseBackupCode((current) => !current);
            setTotpCode('');
            setBackupCode('');
            setError(null);
          }}
          className="text-sm text-legend-teal-tint hover:text-legend-paper"
        >
          {useBackupCode ? tUi(uiLanguage, 'mfaUseAuthenticator') : tUi(uiLanguage, 'mfaUseBackupCode')}
        </button>
      )}

      {error && (
        <div className="text-sm text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={resetMfaState}
          className="px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-900"
          disabled={loading}
        >
          {tUi(uiLanguage, 'mfaBackToSignIn')}
        </button>
        <button
          type="submit"
          disabled={loading || (!useBackupCode && totpCode.length !== 6) || (useBackupCode && !backupCode.trim())}
          className="flex-1 px-4 py-2 bg-legend-teal hover:bg-legend-teal-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
        >
          {loading
            ? tUi(uiLanguage, 'mfaVerifying')
            : step === 'mfa_enroll'
              ? tUi(uiLanguage, 'mfaCompleteSetup')
              : tUi(uiLanguage, 'mfaVerify')}
        </button>
      </div>
    </form>
  );

  const renderBackupCodes = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-300">{tUi(uiLanguage, 'mfaBackupCodesInstructions')}</p>
      <div className="grid grid-cols-2 gap-2 bg-legend-deep border border-legend-teal/25 rounded-lg p-4 font-mono text-sm text-legend-teal-tint">
        {backupCodes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onSuccess(getStoredAuthProfile())}
        className="w-full px-4 py-2 bg-legend-teal hover:bg-legend-teal-dark text-white font-semibold rounded-lg"
      >
        {tUi(uiLanguage, 'mfaContinueToApp')}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-legend-deep text-legend-paper flex items-center justify-center px-4">
      <div className="w-full max-w-sm border border-legend-teal/25 rounded-2xl bg-legend-navy p-8 shadow-2xl">
        <img
          src="/branding/legend-sovereign-wordmark-navy.png"
          alt="Legend Sovereign"
          className="h-20 mx-auto mb-6 object-contain"
        />
        {step === 'credentials' && renderCredentialsForm()}
        {(step === 'mfa_enroll' || step === 'mfa_verify') && renderMfaForm()}
        {step === 'backup_codes' && renderBackupCodes()}
      </div>
    </div>
  );
}
